import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TokenService } from '../auth/token.service';
import { ToastMessageService } from '../utilities/toast-message.service';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  constructor(
    private toastMessage: ToastMessageService,
    private tokenService: TokenService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse | Error) => {
        const authorization = request.headers.get('Authorization');
        // BackupApiInterceptor keeps the HTTP status but intentionally wraps
        // the backend body into Error.message. Accept both shapes so the
        // legacy session-timeout response cannot leave the POS looking logged
        // in merely because the backup layer handled it first.
        const legacySessionTimeout =
          (error as any)?.status === 500 &&
          ((error as any)?.error?.details?.[0] ===
            'No Logged In User Found' ||
            (error as any)?.message === 'No Logged In User Found');
        const explicitUnauthorized =
          (error as any)?.status === 401 ||
          (error as any)?.error === 'Unauthorized' ||
          (error as any)?.message === 'Unauthorized';
        // A generic 403 can be a legitimate business permission rejection.
        // The profile endpoint, however, is the session validation endpoint:
        // a 403 there means the current authenticated session is unusable.
        const profileForbidden =
          (error as any)?.status === 403 &&
          /\/user\/profile(?:\?|$)/.test(request.url);
        const sessionRejected =
          !!authorization &&
          (explicitUnauthorized || legacySessionTimeout || profileForbidden);

        if (sessionRejected) {
          return from(
            this.tokenService.invalidateIfCurrent(authorization),
          ).pipe(
            // invalidateIfCurrent clears the in-memory credential and emits
            // the session event before awaiting Preferences removal. If that
            // durable cleanup fails, keep treating the session as invalidated
            // while preserving the backend HTTP error for the request caller.
            catchError(() => of(true)),
            switchMap((invalidated) => {
              if (invalidated && legacySessionTimeout) {
                this.toastMessage.errorToastWithClose(
                  'Session timeout, Please login again',
                );
              }
              return throwError(() => this.wrapError(error));
            }),
          );
        }

        // Network failures, timeouts and server errors never destroy a valid
        // local session. Callers keep the original HTTP status so they can
        // retry or show the appropriate offline state.
        return throwError(() => this.wrapError(error));
      }),
    );
  }

  private wrapError(error: HttpErrorResponse | Error): Error {
    const serverDetail =
      (error as any)?.error?.details?.[0] ||
      (error as any)?.error?.message ||
      (error as any)?.message;
    const errorMessage =
      typeof serverDetail === 'string' && serverDetail.trim()
        ? serverDetail
        : 'Something went wrong.';
    const wrappedError = new Error(errorMessage);
    (wrappedError as any).status = (error as any)?.status;
    return wrappedError;
  }
}
